import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { BrandLogo } from "@/components/BrandLogo";
import { Users, FlaskConical, BookOpen, Home, LogOut, BarChart3, ScrollText, CreditCard, Building2, DollarSign, Bug, LineChart } from "lucide-react";

const navItems = [
  { to: "/admin/overview", label: "Overview", testid: "admin-nav-overview", Icon: BarChart3 },
  { to: "/admin/analytics", label: "Analytics", testid: "admin-nav-analytics", Icon: LineChart },
  { to: "/admin/users", label: "Users", testid: "admin-nav-users", Icon: Users },
  { to: "/admin/subscribers", label: "Subscribers", testid: "admin-nav-subscribers", Icon: DollarSign },
  { to: "/admin/teams", label: "Teams", testid: "admin-nav-teams", Icon: Building2 },
  { to: "/admin/billing", label: "Billing", testid: "admin-nav-billing", Icon: CreditCard },
  { to: "/admin/audit", label: "Audit log", testid: "admin-nav-audit", Icon: ScrollText },
  { to: "/admin/errors", label: "Crash reports", testid: "admin-nav-errors", Icon: Bug },
  { to: "/admin/simulators", label: "Simulators", testid: "admin-nav-simulators", Icon: FlaskConical },
  { to: "/admin/reading", label: "Further Reading", testid: "admin-nav-reading", Icon: BookOpen },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const doLogout = async () => {
    await logout();
    navigate("/sections");
  };

  return (
    <div className="min-h-screen bg-[#0b1220]">
      <header className="border-b border-slate-800 bg-[#0e1726]">
        <div className="max-w-7xl mx-auto px-6 py-3.5 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <BrandLogo />
            <div className="hidden sm:block border-l border-slate-700 pl-3">
              <div className="text-sm font-semibold tracking-tight text-white">Admin console</div>
              <div className="text-[11px] text-slate-400 -mt-0.5">Content & user management</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-400 hidden sm:inline" data-testid="admin-user">{user?.email}</span>
            <Link to="/sections" data-testid="admin-back-app-btn">
              <Button variant="outline" size="sm" className="bg-transparent border-slate-700 text-slate-200 hover:bg-slate-800">
                <Home className="w-4 h-4 mr-1.5" /> App
              </Button>
            </Link>
            <Button variant="ghost" size="sm" onClick={doLogout} data-testid="admin-logout-btn" className="text-slate-400 hover:text-white">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col lg:flex-row gap-6">
        <nav className="lg:w-56 shrink-0">
          <div className="flex lg:flex-col gap-1.5 overflow-x-auto lg:overflow-visible -mx-1 px-1 pb-2 lg:pb-0" data-testid="admin-nav">
            {navItems.map(({ to, label, testid, Icon }) => (
              <NavLink
                key={to}
                to={to}
                data-testid={testid}
                className={({ isActive }) =>
                  `flex items-center gap-2.5 px-3.5 py-2.5 rounded-lg text-sm font-medium transition-colors shrink-0 whitespace-nowrap lg:w-full ${
                    isActive ? "bg-[#0047AB] text-white" : "text-slate-300 hover:bg-slate-800"
                  }`
                }
              >
                <Icon className="w-4 h-4" /> {label}
              </NavLink>
            ))}
          </div>
        </nav>
        <main className="flex-1 min-w-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
